<template>
  <div class="vdm-list-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">
        <i class="el-icon-s-grid"></i>
        kkady 搜索商品
      </h2>
      <div class="page-actions">
        <el-button
          type="success"
          size="small"
          @click="showBatchInputDialog"
          icon="el-icon-document-add">
          批量输入产品编号
        </el-button>
        <el-button
          type="primary"
          size="small"
          @click="fetchProductList"
          :loading="loading"
          icon="el-icon-refresh">
          刷新数据
        </el-button>
      </div>
    </div>

    <!-- 搜索筛选区域 -->
    <el-card class="filter-card" size="small">
      <el-form :model="searchForm" :inline="true" size="small">
 
        <el-form-item label="关键词">
          <el-input
            v-model="searchForm.prod_name"
            placeholder="关键词"
            clearable
            style="width: 200px"
            @clear="handleSearch"
            @keyup.enter.native="handleSearch">
          </el-input>
        </el-form-item>

        <el-form-item label="分类">
          <el-button
            type="primary"
            plain
            @click="showCategorySelector"
            style="width: 200px"
            :loading="travelCategoryLoading">
            <span v-if="selectedCategoryName">{{ selectedCategoryName }}</span>
            <span v-else>请选择分类</span>
          </el-button>
          <el-button
            v-if="selectedCategoryName"
            type="text"
            @click="clearCategory"
            style="margin-left: 10px">
            清除
          </el-button>
        </el-form-item>


        <el-form-item>
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
          <el-button @click="handleReset" icon="el-icon-refresh-left">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 产品列表 -->
    <el-card class="product-list-card">
      <div slot="header" class="card-header">
        <div class="header-left">
          <el-checkbox
            v-model="selectAll"
            :indeterminate="isIndeterminate"
            @change="handleSelectAll">
            <span>全选</span>
          </el-checkbox>
          <span class="selected-count" v-if="selectedProducts.length > 0">
            已选 {{ selectedProducts.length }} 项
          </span>
        </div>
        <div class="header-actions">
          <el-tag type="info" size="small">
            B2B价格区间: {{ priceRange.min }} - {{ priceRange.max }} CNY
          </el-tag>
          <el-button
            type="success"
            size="small"
            :disabled="selectedProducts.length === 0"
            @click="batchSubmitProducts"
            :loading="batchSubmitting"
            icon="el-icon-check">
            批量提交 ({{ selectedProducts.length }})
          </el-button>
        </div>
      </div>

      <!-- 产品网格展示 -->
      <div class="product-grid" v-loading="loading">
        <div
          v-for="product in filteredList"
          :key="product.prod_no"
          class="product-item"
          :class="{ 'is-selected': isProductSelected(product) }">

          <!-- 选择复选框 -->
          <div class="product-checkbox">
            <el-checkbox
              v-model="product.checked"
              @change="handleProductCheck(product)">
            </el-checkbox>
          </div>

          <!-- 产品内容区域 -->
          <div class="product-content" @click="showProductDetail(product)">

          <!-- 产品图片 -->
          <div class="product-image" v-if="0">
            <img
              :src="getImageUrl(product.prod_img_url)"
              :alt="product.prod_name"
              @error="handleImageError">
            <div class="product-type-badge">
              {{ getProductTypeLabel(product.prod_type) }}
            </div>
          </div>

          <!-- 产品信息 -->
          <div class="product-info">
            <div class="product-header">
              <h3 class="product-name" :title="product.prod_name">
                {{ product.prod_name }}
              </h3>
              <div class="product-meta">
                <el-tag size="mini" type="info">#{{ product.prod_no }}</el-tag>
              </div>
            </div>

            <!-- 排序字段 - 可内联编辑 -->
            <div class="product-sort-row">
              <span class="sort-label">排序:</span>
              <el-input-number
                v-model="product.sort"
                :min="0"
                :max="9999"
                size="mini"
                :disabled="product.sortSaving"
                @change="(value) => handleSortChange(product, value)"
                controls-position="right"
                class="sort-input">
              </el-input-number>
              <i v-if="product.sortSaving" class="el-icon-loading sort-loading"></i>
              <i v-else-if="product.sortSaved" class="el-icon-check sort-success"></i>
            </div>

            <!-- 目的地标签 -->
            <div class="destinations">
              <el-tag
                v-for="dest in product.destinations"
                :key="dest.code"
                size="mini"
                type="primary"
                class="destination-tag">
                <i class="el-icon-location-outline"></i>
                {{ dest.name }}
              </el-tag>
            </div>

            <!-- 评分信息 -->
            <div class="rating-info" v-if="product.rating_count > 0">
              <el-rate
                v-model="product.avg_rating_star"
                disabled
                show-score
                text-color="#ff9900"
                score-template="{value}">
              </el-rate>
              <span class="rating-count">({{ product.rating_count }}条评价)</span>
            </div>

            <!-- 产品简介 -->
            <div class="introduction">
              <p :title="product.introduction">{{ getShortIntroduction(product.introduction) }}</p>
            </div>

            <!-- 价格信息 -->
            <div class="price-info">
              <div class="price-row">
                <span class="price-label">B2B价格:</span>
                <span class="price-value b2b-price">¥{{ product.b2b_price }}</span>
              </div>
              <div class="price-row">
                <span class="price-label">B2C价格:</span>
                <span class="price-value b2c-price">¥{{ product.b2c_price }}</span>
              </div>
              <div class="price-row">
                <span class="price-label">利润空间:</span>
                <span class="price-value profit" :class="getProfitClass(product)">
                  ¥{{ (product.b2c_price - product.b2b_price).toFixed(2) }}
                  <span class="profit-percent">({{ getProfitPercent(product) }}%)</span>
                </span>
              </div>
            </div>

            <!-- 产品状态 -->
            <div class="product-status">
              <el-tag
                :type="product.instant_booking ? 'success' : 'info'"
                size="mini">
                {{ product.instant_booking ? '即时预订' : '需确认' }}
              </el-tag>
              <el-tag
                type="warning"
                size="mini"
                v-if="product.earliest_sale_date">
                开售: {{ formatDate(product.earliest_sale_date) }}
              </el-tag>
            </div>

            <!-- 操作按钮 -->
            <div class="product-actions">
              <el-button
                type="success"
                size="mini"
                :loading="product.submitting"
                @click.stop="submitProductFromList(product)"
                icon="el-icon-check">
                提交商品
              </el-button>
            </div>
          </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="filteredList.length === 0 && !loading" class="empty-state">
          <i class="el-icon-box"></i>
          <p>暂无产品数据</p>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination-container" v-if="total > 0">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[12, 24, 48, 96]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 产品详情弹窗 -->
    <el-dialog
      title="产品详情"
      :visible.sync="detailDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      class="product-detail-dialog">
      <div v-if="selectedProduct" class="product-detail">
        <!-- 基础产品信息（从列表获取） -->
        <div class="basic-info">
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="detail-image">
                <img :src="getImageUrl(selectedProduct.prod_img_url)" :alt="selectedProduct.prod_name">
              </div>
            </el-col>
            <el-col :span="16">
              <div class="detail-info">
                <h2>{{ selectedProduct.prod_name }}</h2>
                <div class="detail-meta">
                  <el-tag size="medium">产品编号: {{ selectedProduct.prod_no }}</el-tag>
                  <el-tag size="medium" type="primary">货币: {{ selectedProduct.prod_currency }}</el-tag>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 详细数据加载状态 -->
        <div v-if="detailLoading" class="detail-loading">
          <el-skeleton :rows="8" animated />
        </div>

        <!-- 详细数据显示 -->
        <div v-else-if="productDetail" class="detail-content">
          <h3>产品详情</h3>

          <!-- 基础信息 -->
          <div class="detail-section" v-if="hasBasicInfo(productDetail.prod)">
            <h4>基础信息</h4>
            <div class="info-grid">
              <div class="info-item" v-if="productDetail.prod.prod_no">
                <span class="label">产品编号:</span>
                <span class="value">{{ productDetail.prod.prod_no }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.prod_name">
                <span class="label">产品名称:</span>
                <span class="value">{{ productDetail.prod.prod_name }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.prod_type">
                <span class="label">产品类型:</span>
                <span class="value">{{ productDetail.prod.prod_type }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('is_tour')">
                <span class="label">是否旅游产品:</span>
                <span class="value">{{ productDetail.prod.is_tour ? '是' : '否' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.timezone">
                <span class="label">时区:</span>
                <span class="value">{{ productDetail.prod.timezone }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('instant_confirm')">
                <span class="label">即时确认:</span>
                <span class="value">{{ productDetail.prod.instant_confirm ? '是' : '否' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('confirm_order_time')">
                <span class="label">订单确认时间:</span>
                <span class="value">{{ productDetail.prod.confirm_order_time ? '需要' : '不需要' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('is_cancel_free')">
                <span class="label">免费取消:</span>
                <span class="value">{{ productDetail.prod.is_cancel_free ? '是' : '否' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.online_s_date">
                <span class="label">在线开始日期:</span>
                <span class="value">{{ productDetail.prod.online_s_date }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.online_e_date">
                <span class="label">在线结束日期:</span>
                <span class="value">{{ productDetail.prod.online_e_date }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.tourism_product_type">
                <span class="label">旅游产品类型:</span>
                <span class="value">{{ productDetail.prod.tourism_product_type }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('is_translate_complete')">
                <span class="label">翻译完成:</span>
                <span class="value">{{ productDetail.prod.is_translate_complete ? '是' : '否' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('have_translate')">
                <span class="label">有翻译:</span>
                <span class="value">{{ productDetail.prod.have_translate ? '是' : '否' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.inquiry_locale">
                <span class="label">查询语言环境:</span>
                <span class="value">{{ productDetail.prod.inquiry_locale }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('is_all_sold_out')">
                <span class="label">是否全部售完:</span>
                <span class="value">{{ productDetail.prod.is_all_sold_out ? '是' : '否' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hasOwnProperty('instant_booking')">
                <span class="label">即时预订:</span>
                <span class="value">{{ productDetail.prod.instant_booking ? '是' : '否' }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.order_count !== undefined && productDetail.prod.order_count !== null">
                <span class="label">订单数量:</span>
                <span class="value">{{ productDetail.prod.order_count }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.days !== undefined && productDetail.prod.days !== null">
                <span class="label">天数:</span>
                <span class="value">{{ productDetail.prod.days }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.hours !== undefined && productDetail.prod.hours !== null">
                <span class="label">小时:</span>
                <span class="value">{{ productDetail.prod.hours }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.minute !== undefined && productDetail.prod.minute !== null">
                <span class="label">分钟:</span>
                <span class="value">{{ productDetail.prod.minute }}</span>
              </div>
              <div class="info-item" v-if="productDetail.prod.prod_currency">
                <span class="label">产品货币:</span>
                <span class="value">{{ productDetail.prod.prod_currency }}</span>
              </div>
            </div>
          </div>

          <!-- 价格信息 -->
          <div class="detail-section" v-if="hasPriceInfo(productDetail.prod)">
            <h4>价格信息</h4>
            <div class="price-info">
              <div class="price-row" v-if="productDetail.prod.b2c_min_price !== undefined && productDetail.prod.b2c_min_price !== null">
                <span>B2C最低价格:</span>
                <span class="price">¥{{ productDetail.prod.b2c_min_price }}</span>
              </div>
              <div class="price-row" v-if="productDetail.prod.b2b_min_price !== undefined && productDetail.prod.b2b_min_price !== null">
                <span>B2B最低价格:</span>
                <span class="price">¥{{ productDetail.prod.b2b_min_price }}</span>
              </div>
              <div class="price-row" v-if="productDetail.prod.b2c_price !== undefined && productDetail.prod.b2c_price !== null">
                <span>B2C价格:</span>
                <span class="price">¥{{ productDetail.prod.b2c_price }}</span>
              </div>
              <div class="price-row" v-if="productDetail.prod.b2b_price !== undefined && productDetail.prod.b2b_price !== null">
                <span>B2B价格:</span>
                <span class="price">¥{{ productDetail.prod.b2b_price }}</span>
              </div>
            </div>
          </div>

          <!-- 语言信息 -->
          <div class="detail-section" v-if="safeArray(productDetail.prod.guide_lang_list).length > 0">
            <h4>导游语言</h4>
            <div class="lang-list">
              <el-tag v-for="lang in safeArray(productDetail.prod.guide_lang_list)" :key="lang" size="small" style="margin-right: 5px;">
                {{ lang }}
              </el-tag>
            </div>
          </div>

          <div class="detail-section" v-if="safeArray(productDetail.prod.voice_guide_lang).length > 0">
            <h4>语音导游语言</h4>
            <div class="lang-list">
              <el-tag v-for="lang in safeArray(productDetail.prod.voice_guide_lang)" :key="lang" size="small" type="success" style="margin-right: 5px;">
                {{ lang }}
              </el-tag>
            </div>
          </div>

          <!-- 城市和目的地信息 -->
          <div class="detail-section" v-if="safeArray(productDetail.prod.cities).length > 0">
            <h4>城市信息</h4>
            <div class="cities-info">
              <div v-for="city in safeArray(productDetail.prod.cities)" :key="city.city_code || city.name" class="city-item">
                <el-tag type="info">{{ city.name || '未知城市' }}</el-tag>
                <span class="city-code" v-if="city.city_code">({{ city.city_code }})</span>
              </div>
            </div>
          </div>

          <div class="detail-section" v-if="productDetail.prod.destinations && productDetail.prod.destinations.destinations && safeArray(productDetail.prod.destinations.destinations).length > 0">
            <h4>目的地信息</h4>
            <div class="destinations">
              <el-tag
                v-for="dest in safeArray(productDetail.prod.destinations.destinations)"
                :key="dest.code || dest.name"
                class="destination-tag"
                type="warning"
              >
                <i class="el-icon-location"></i>
                {{ dest.name || '未知目的地' }}
              </el-tag>
            </div>
          </div>

          <!-- 标签信息 -->
          <div class="detail-section" v-if="safeArray(productDetail.prod.tag).length > 0">
            <h4>产品标签</h4>
            <div class="tags">
              <el-tag v-for="tag in safeArray(productDetail.prod.tag)" :key="tag" size="small" style="margin-right: 5px;">
                {{ tag }}
              </el-tag>
            </div>
          </div>

          <!-- 产品类别 -->
          <div class="detail-section" v-if="productDetail.prod.product_category">
            <h4>产品类别</h4>
            <div class="category-info">
              <div class="category-item">
                <span class="label">主要类别:</span>
                <span class="value">{{ productDetail.prod.product_category.main }}</span>
              </div>
              <div class="category-item" v-if="productDetail.prod.product_category.sub && productDetail.prod.product_category.sub.length > 0">
                <span class="label">子类别:</span>
                <div class="sub-categories">
                  <el-tag v-for="sub in productDetail.prod.product_category.sub" :key="sub" size="mini" style="margin-right: 5px;">
                    {{ sub }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>

          <!-- 产品图片 -->
          <div class="detail-section" v-if="productDetail.prod.img_list && productDetail.prod.img_list.length > 0">
            <h4>产品图片 ({{ productDetail.prod.img_list.length }}张)</h4>
            <div class="simple-image-list">
              <img
                v-for="(img, index) in productDetail.prod.img_list.slice(0, 3)"
                :key="index"
                :src="img"
                :alt="'产品图片' + (index + 1)"
                style="max-width: 200px; height: 150px; object-fit: cover; margin: 5px; border-radius: 4px; cursor: pointer;"
                @click="previewImage(img)" />
              <div v-if="productDetail.prod.img_list.length > 3" style="padding: 5px;">
                还有 {{ productDetail.prod.img_list.length - 3 }} 张图片...
              </div>
            </div>
          </div>

          <!-- 产品介绍 -->
          <div class="detail-section" v-if="productDetail.prod.introduction">
            <h4>产品介绍</h4>
            <div v-html="productDetail.prod.introduction" style="line-height: 1.6; color: #606266;"></div>
          </div>

          <!-- 用户评价 -->
          <div class="detail-section" v-if="hasCommentInfo(productDetail.prod.prod_comment_info)">
            <h4>用户评价</h4>
            <p v-if="productDetail.prod.prod_comment_info.avg_scores"><strong>平均评分:</strong> {{ productDetail.prod.prod_comment_info.avg_scores }}/5</p>
            <p v-if="productDetail.prod.prod_comment_info.total_scores"><strong>评价总数:</strong> {{ productDetail.prod.prod_comment_info.total_scores }}条</p>
            <p v-if="productDetail.prod.prod_comment_info.comment_record"><strong>评价数量:</strong> {{ productDetail.prod.prod_comment_info.comment_record }}条</p>
          </div>

          <!-- 营销设置 -->
          <div class="detail-section">
            <h4>销售地区</h4>
            <div class="marketing-grid">
              <div
                v-for="(setting, country) in productDetail.prod_marketing"
                :key="country"
                class="marketing-item">
                <div class="country-flag">{{ country }}</div>
                <div class="settings">
                  <el-tag size="mini" :type="setting.is_search ? 'success' : 'info'">
                    搜索: {{ setting.is_search ? '开启' : '关闭' }}
                  </el-tag>
                  <el-tag size="mini" :type="setting.is_sale ? 'success' : 'info'">
                    销售: {{ setting.is_sale ? '开启' : '关闭' }}
                  </el-tag>
                  <el-tag size="mini" :type="setting.is_show ? 'success' : 'info'">
                    显示: {{ setting.is_show ? '开启' : '关闭' }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>

          <!-- 凭证使用方式 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_EXCHANGE">
            <h4>凭证使用方式</h4>
            <div class="description-module">
              <div v-if="safeGet(productDetail.prod, 'description_module.PMDL_EXCHANGE.content.properties.exchange_type.desc')">
                <p><strong>使用方式:</strong> {{ safeGet(productDetail.prod, 'description_module.PMDL_EXCHANGE.content.properties.exchange_type.desc') }}</p>
              </div>
              <div v-if="safeGet(productDetail.prod, 'description_module.PMDL_EXCHANGE.content.properties.description.desc')">
                <p><strong>详细说明:</strong> {{ safeGet(productDetail.prod, 'description_module.PMDL_EXCHANGE.content.properties.description.desc') }}</p>
              </div>
            </div>
          </div>

          <!-- 费用包含/不包含 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_INC_NINC">
            <h4>费用包含/不包含</h4>
            <div class="description-module">
              <div v-if="safeArray(safeGet(productDetail.prod, 'description_module.PMDL_INC_NINC.content.properties.include_item.list')).length > 0">
                <p><strong>费用包含:</strong></p>
                <ul>
                  <li v-for="(item, index) in safeArray(safeGet(productDetail.prod, 'description_module.PMDL_INC_NINC.content.properties.include_item.list'))" :key="index">
                    {{ item ? (item.desc || item) : '' }}
                  </li>
                </ul>
              </div>
              <div v-if="safeArray(safeGet(productDetail.prod, 'description_module.PMDL_INC_NINC.content.properties.not_include_item.list')).length > 0">
                <p><strong>费用不包含:</strong></p>
                <ul>
                  <li v-for="(item, index) in safeArray(safeGet(productDetail.prod, 'description_module.PMDL_INC_NINC.content.properties.not_include_item.list'))" :key="index">
                    {{ item ? (item.desc || item) : '' }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- 使用期限 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_USE_VALID">
            <h4>使用期限</h4>
            <div class="description-module">
              <div v-if="safeGet(productDetail.prod, 'description_module.PMDL_USE_VALID.content.properties.exchange.desc')">
                <p>{{ safeGet(productDetail.prod, 'description_module.PMDL_USE_VALID.content.properties.exchange.desc') }}</p>
              </div>
            </div>
          </div>

          <!-- 取消政策 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_REFUND_POLICY">
            <h4>取消政策</h4>
            <div class="description-module">
              <div v-if="safeGet(productDetail.prod, 'description_module.PMDL_REFUND_POLICY.content.properties.policy_type')">
                <p><strong>{{ safeString(safeGet(productDetail.prod, 'description_module.PMDL_REFUND_POLICY.content.properties.policy_type.title'), '取消政策') }}:</strong> {{ safeString(safeGet(productDetail.prod, 'description_module.PMDL_REFUND_POLICY.content.properties.policy_type.desc')) }}</p>
              </div>
            </div>
          </div>

          <!-- 注意事项 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_NOTICE">
            <h4>注意事项</h4>
            <div class="description-module">
              <div v-if="safeArray(safeGet(productDetail.prod, 'description_module.PMDL_NOTICE.content.properties.cust_reminds.list')).length > 0">
                <ul>
                  <li v-for="(item, index) in safeArray(safeGet(productDetail.prod, 'description_module.PMDL_NOTICE.content.properties.cust_reminds.list'))" :key="index">
                    {{ item ? (item.desc || item) : '' }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- 图文介绍 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_GRAPHIC">
            <h4>图文介绍</h4>
            <div class="description-module">
              <div v-for="(item, index) in safeArray(safeGet(productDetail.prod, 'description_module.PMDL_GRAPHIC.content.list'))" :key="index" class="graphic-item">
                <div v-if="safeArray(item.media).length > 0" class="graphic-media">
                  <img
                    v-for="(media, mediaIndex) in safeArray(item.media)"
                    :key="mediaIndex"
                    :src="media.source_content"
                    :alt="'图文介绍' + (index + 1)"
                    style="max-width: 300px; height: 200px; object-fit: cover; margin: 5px; border-radius: 4px; cursor: pointer;"
                    @click="previewImage(media.source_content)"
                    @error="handleImageError" />
                </div>
                <p class="graphic-desc" v-if="item && item.desc">{{ item.desc }}</p>
              </div>
            </div>
          </div>

          <!-- 购买须知 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_PURCHASE_SUMMARY">
            <h4>购买须知</h4>
            <div class="description-module">
              <div v-if="safeGet(productDetail.prod, 'description_module.PMDL_PURCHASE_SUMMARY.content.desc')" v-html="safeGet(productDetail.prod, 'description_module.PMDL_PURCHASE_SUMMARY.content.desc')"></div>
            </div>
          </div>

          <!-- 商品说明 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_INTRODUCE_SUMMARY">
            <h4>商品说明</h4>
            <div class="description-module">
              <div v-if="safeGet(productDetail.prod, 'description_module.PMDL_INTRODUCE_SUMMARY.content.desc')" v-html="safeGet(productDetail.prod, 'description_module.PMDL_INTRODUCE_SUMMARY.content.desc')"></div>
            </div>
          </div>

          <!-- 体验地点 -->
          <div class="detail-section" v-if="productDetail.prod.description_module && productDetail.prod.description_module.PMDL_EXPERIENCE_LOCATION">
            <h4>体验地点</h4>
            <div class="description-module">
              <div v-for="(location, index) in safeArray(safeGet(productDetail.prod, 'description_module.PMDL_EXPERIENCE_LOCATION.content.list'))" :key="index" class="location-item">
                <div v-if="location && location.location_name" class="location-name">
                  <h5>{{ safeGet(location, 'location_name.desc') }}</h5>
                </div>
                <div v-if="location && location.latlng" class="location-address">
                  <p><strong>地址:</strong> {{ safeGet(location, 'latlng.desc') }}</p>
                  <p><strong>坐标:</strong> {{ safeGet(location, 'latlng.latitude') }}, {{ safeGet(location, 'latlng.longitude') }}</p>
                </div>
                <div v-if="location && location.arrival_desc" class="arrival-desc">
                  <p><strong>如何抵达:</strong></p>
                  <p v-html="safeGet(location, 'arrival_desc.desc', '').replace && safeGet(location, 'arrival_desc.desc', '').replace('\n', '<br>')"></p>
                </div>
                <div v-if="location && location.active_time" class="active-time">
                  <p><strong>开放时间:</strong></p>
                  <table class="time-table" v-if="safeArray(location.active_time.list).length > 0">
                    <thead>
                      <tr>
                        <th v-for="(key, thKey) in safeGet(location, 'active_time.table_key_langs', {})" :key="thKey">{{ key }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(time, tIndex) in safeArray(location.active_time.list)" :key="tIndex">
                        <td>{{ safeGet(time, 'week_title.desc') }}</td>
                        <td>{{ safeGet(time, 'start_time.desc') }} - {{ safeGet(time, 'end_time.desc') }}</td>
                        <td>{{ safeGet(time, 'last_admission.desc') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="location && location.photo && safeArray(location.photo.media).length > 0" class="location-photo">
                  <img
                    v-for="(photo, photoIndex) in location.photo.media"
                    :key="photoIndex"
                    :src="photo.source_content"
                    :alt="'地点图片' + (index + 1)"
                    style="max-width: 200px; height: 150px; object-fit: cover; margin: 5px; border-radius: 4px; cursor: pointer;"
                    @click="previewImage(photo.source_content)" />
                </div>
              </div>
            </div>
          </div>

          <!-- 套餐信息 -->
          <div class="detail-section" v-if="safeArray(productDetail.pkg).length > 0">
            <h4>套餐信息</h4>
            <div v-for="pkg in safeArray(productDetail.pkg)" :key="pkg.pkg_no || pkg.pkg_name" style="margin-bottom: 10px; padding: 10px; background: #f8f9fa; border-radius: 4px;">
              <p v-if="pkg.pkg_name"><strong>套餐名称:</strong> {{ pkg.pkg_name }}</p>
              <p v-if="pkg.pkg_no"><strong>套餐编号:</strong> {{ pkg.pkg_no }}</p>
              <p v-if="pkg.b2b_min_price !== undefined && pkg.b2b_min_price !== null"><strong>B2B价格:</strong> ¥{{ pkg.b2b_min_price }}</p>
              <p v-if="pkg.b2c_min_price !== undefined && pkg.b2c_min_price !== null"><strong>B2C价格:</strong> ¥{{ pkg.b2c_min_price }}</p>
              <p v-if="pkg.sale_s_date || pkg.sale_e_date"><strong>销售日期:</strong> {{ pkg.sale_s_date || '开始日期未设置' }} 至 {{ pkg.sale_e_date || '结束日期未设置' }}</p>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="action-buttons">
            <el-button
              type="info"
              size="small"
              @click="toggleRawData"
              style="margin-right: 10px">
              {{ showRawData ? '隐藏' : '显示' }}原始数据
            </el-button>

            <el-button
              type="success"
              size="small"
              :loading="submitLoading"
              @click="submitProduct"
              icon="el-icon-check">
              提交商品到后端
            </el-button>
          </div>

          <!-- 原始数据 -->
          <div v-if="showRawData" class="raw-data-section">
            <div class="raw-data-container">
              <pre>{{ JSON.stringify(productDetail, null, 2) }}</pre>
            </div>
          </div>
        </div>

        <!-- 获取失败时的显示 -->
        <div v-else-if="!detailLoading" class="no-detail-data">
          <el-empty description="暂无详细数据或获取失败"></el-empty>
        </div>
      </div>
    </el-dialog>

    <!-- 商品分类选择对话框 -->
    <el-dialog
      title="选择商品分类"
      :visible.sync="categoryDialogVisible"
      width="500px"
      :close-on-click-modal="false">
      <div v-loading="categoryLoading">
        <el-form-item label="城市">
          <el-select
            v-model="selectedCityId"
            placeholder="请选择城市"
            clearable
            style="width: 100%">
            <el-option
              v-for="city in cities"
              :key="city.id"
              :label="city.title"
              :value="city.id"
            />
          </el-select>
        </el-form-item>
        <el-tree
          :data="categoryList"
          :props="{ children: 'children', label: 'title' }"
          show-checkbox
          node-key="id"
          :check-strictly="true"
          ref="categoryTree"
          default-expand-all
          @check="handleCategoryCheck">
        </el-tree>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="categoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSubmitWithCategory" :disabled="!selectedCategoryId">确定</el-button>
      </div>
    </el-dialog>

    <!-- 分类选择弹窗 -->
    <el-dialog
      title="选择分类"
      :visible.sync="categorySelectorVisible"
      width="600px"
      :close-on-click-modal="false">
      <el-table
        :data="travelCategoryList"
        style="width: 100%"
        row-key="code"
        border
        :tree-props="{children: 'children', hasChildren: 'hasChildren'}"
        v-loading="travelCategoryLoading"
        @row-click="handleCategoryRowClick"
        highlight-current-row>
        <el-table-column prop="code_name" label="分类名称" width="250"></el-table-column>
        <el-table-column prop="code" label="分类编码" width="180"></el-table-column>
        <el-table-column prop="level" label="层级" width="80" align="center"></el-table-column>
      </el-table>
      <div slot="footer" class="dialog-footer">
        <el-button @click="categorySelectorVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmCategorySelect" :disabled="!selectedCategoryCode">确定</el-button>
      </div>
    </el-dialog>

    <!-- 批量输入产品编号弹窗 -->
    <el-dialog
      title="批量输入产品编号"
      :visible.sync="batchInputDialogVisible"
      width="600px"
      :close-on-click-modal="false">
      <el-alert
        title="请输入产品编号，每行一个。系统会自动处理空格和换行。"
        type="info"
        :closable="false"
        style="margin-bottom: 15px">
      </el-alert>
      <el-input
        type="textarea"
        :rows="10"
        placeholder="请输入产品编号，每行一个。例如：&#10;PROD001&#10;PROD002&#10;PROD003"
        v-model="batchInputText">
      </el-input>
      <div style="margin-top: 10px; color: #909399; font-size: 12px;">
        <i class="el-icon-info"></i>
        已输入 {{ getParsedProductCount() }} 个产品编号
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchInputDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchInput" :loading="batchInputSubmitting" :disabled="getParsedProductCount() === 0">
          下一步：选择分类 ({{ getParsedProductCount() }})
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { vdmApi, travelCategoryApi, kkdayProductApi } from '@/api/index.js'

export default {
  name: 'VdmListView',
  data() {
    return {
      loading: false,
      productList: [],
      filteredList: [],
      total: 0, // 总条数
      searchForm: {
        prod_no: '',
        prod_name: '',
        destination: '',
        category_code: ''
      },
      currentPage: 1,
      pageSize: 12,
      detailDialogVisible: false,
      selectedProduct: null,
      productDetail: null,
      detailLoading: false,
      showRawData: false,
      submitLoading: false,
      // 分类选择相关
      categoryDialogVisible: false,
      categoryList: [],
      selectedCategoryId: null,
      selectedCategoryPath: [], // 存储分类路径
      pendingSubmitProduct: null, // 待提交的商品
      categoryLoading: false,
      // 搜索分类相关
      travelCategoryList: [], // 旅游分类列表
      travelCategoryLoading: false,
      // 分类选择器相关
      categorySelectorVisible: false,
      selectedCategoryName: '', // 选中的分类名称
      selectedCategoryCode: '', // 选中的分类编码
      // 城市选择相关
      cities: [],
      selectedCityId: null,
      // 批量选择相关
      selectAll: false,
      isIndeterminate: false,
      selectedProducts: [],
      batchSubmitting: false,
      // 批量输入产品编号相关
      batchInputDialogVisible: false,
      batchInputText: '',
      batchInputSubmitting: false
    }
  },
  computed: {
    // 价格范围
    priceRange() {
      if (this.filteredList.length === 0) {
        return { min: 0, max: 0 }
      }
      const prices = this.filteredList.map(item => item.b2b_price)
      return {
        min: Math.min(...prices),
        max: Math.max(...prices)
      }
    }
  },
  mounted() {
    this.fetchProductList()
    this.fetchTravelCategoryList()
    this.fetchCities()
  },
  methods: {
    // 获取产品列表
    async fetchProductList() {
      this.loading = true
      try {
        // 构建搜索参数
        const params = {
          // start 参数计算：(当前页 - 1) * 每页条数
          start: (this.currentPage - 1) * this.pageSize,
          page_size: this.pageSize
        }
        if (this.searchForm.prod_no) params.prod_no = this.searchForm.prod_no
        if (this.searchForm.prod_name) params.prod_name = this.searchForm.prod_name
        if (this.searchForm.destination) params.destination = this.searchForm.destination
        if (this.searchForm.category_code) params.category_code = this.searchForm.category_code

        console.log('发送搜索参数:', params)

        const res = await vdmApi.getProductList(params)
        if (res && res.code === 1) {
          this.productList = res.data.list.prods || []
          this.filteredList = [...this.productList]
          this.total = (res.data.list.metadata && res.data.list.metadata.total_count) || 0
        } else {
          this.$message.error(res.message || '获取产品列表失败')
        }
      } catch (error) {
        console.error('获取产品列表失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 获取旅游分类列表
    async fetchTravelCategoryList() {
      this.travelCategoryLoading = true
      try {
        const res = await travelCategoryApi.getList()
        if (res && res.code === 1) {
          this.travelCategoryList = res.data.list || []
        }
      } catch (error) {
        console.error('获取旅游分类失败:', error)
      } finally {
        this.travelCategoryLoading = false
      }
    },

    // 获取城市列表
    async fetchCities() {
      try {
        const res = await vdmApi.getCityList()
        if (res && res.code === 1) {
          this.cities = res.data.list || []
        }
      } catch (error) {
        console.error('获取城市列表失败:', error)
      }
    },

    // 搜索处理
    handleSearch() {
      // 重新从后端获取数据
      this.fetchProductList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        prod_no: '',
        prod_name: '',
        destination: '',
        category_code: ''
      }
      this.selectedCategoryName = ''
      this.selectedCategoryCode = ''
      this.currentPage = 1
      this.fetchProductList()
    },

    // 显示分类选择器
    showCategorySelector() {
      this.categorySelectorVisible = true
      this.selectedCategoryCode = ''
      // 如果数据为空，重新获取
      if (this.travelCategoryList.length === 0) {
        this.fetchTravelCategoryList()
      }
    },

    // 清除分类选择
    clearCategory() {
      this.selectedCategoryName = ''
      this.selectedCategoryCode = ''
      this.searchForm.category_code = ''
      // 不立即搜索，让用户手动点击搜索按钮
    },

    // 处理分类行点击
    handleCategoryRowClick(row, column, event) {
      this.selectedCategoryCode = row.code
      this.selectedCategoryName = row.code_name
    },

    // 确认分类选择
    confirmCategorySelect() {
      if (this.selectedCategoryCode) {
        this.searchForm.category_code = this.selectedCategoryCode
        this.categorySelectorVisible = false
        // 不立即搜索，让用户手动点击搜索按钮
      }
    },

    // 显示产品详情
    async showProductDetail(product) {
      this.selectedProduct = product
      this.productDetail = null
      this.detailDialogVisible = true
      this.detailLoading = true

      try {
        console.log('调用产品详情接口，产品编号:', product.prod_no)
        const res = await vdmApi.getProductDetail(product.prod_no)
        console.log('产品详情接口返回:', res)

        if (res && res.code === 1) {
          this.productDetail = res.data
          console.log('产品详情数据:', this.productDetail)
        } else {
          // 如果没有code字段，可能是直接返回的数据
          if (res && (res.prod || res.data)) {
            this.productDetail = res.data || res
            console.log('产品详情数据(直接):', this.productDetail)
          } else {
            this.$message.error('获取产品详情失败：数据格式不正确')
            console.error('获取产品详情失败，数据格式:', res)
          }
        }
      } catch (error) {
        console.error('获取产品详情失败:', error)
      } finally {
        this.detailLoading = false
      }
    },

    // 切换原始数据显示
    toggleRawData() {
      this.showRawData = !this.showRawData
    },

    // 分页处理
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.clearSelection()
      this.fetchProductList()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.clearSelection()
      this.fetchProductList()
    },

    // 工具方法
    getImageUrl(url) {
      if (!url) return ''
      if (url.startsWith('/')) {
        return `https://api-b2d-10.sit.kkday.com${url}`
      }
      return url
    },

    handleImageError(event) {
      event.target.src = ''
    },

    getProductTypeLabel(type) {
      const typeMap = {
        'CATEGORY_005': '景点门票',
        'CATEGORY_001': '旅游行程',
        'CATEGORY_002': '体验活动'
      }
      return typeMap[type] || '其他'
    },

    getShortIntroduction(intro) {
      if (!intro) return ''
      return intro.length > 100 ? intro.substring(0, 100) + '...' : intro
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = dateStr.substring(0, 4) + '-' +
                   dateStr.substring(4, 6) + '-' +
                   dateStr.substring(6, 8)
      return date
    },

    getProfitPercent(product) {
      if (!product.b2b_price || product.b2b_price === 0) return '0'
      const profit = product.b2c_price - product.b2b_price
      return ((profit / product.b2b_price) * 100).toFixed(1)
    },

    getProfitClass(product) {
      const profitPercent = parseFloat(this.getProfitPercent(product))
      if (profitPercent >= 30) return 'high-profit'
      if (profitPercent >= 15) return 'medium-profit'
      return 'low-profit'
    },

    // 健壮性检查方法
    hasBasicInfo(prod) {
      if (!prod) return false
      return prod.prod_no || prod.prod_name || prod.prod_type ||
             prod.hasOwnProperty('is_tour') || prod.timezone ||
             prod.hasOwnProperty('instant_confirm') || prod.hasOwnProperty('is_cancel_free')
    },

    hasPriceInfo(prod) {
      if (!prod) return false
      return (prod.b2c_min_price !== undefined && prod.b2c_min_price !== null) ||
             (prod.b2b_min_price !== undefined && prod.b2b_min_price !== null) ||
             (prod.b2c_price !== undefined && prod.b2c_price !== null) ||
             (prod.b2b_price !== undefined && prod.b2b_price !== null)
    },

    hasCommentInfo(commentInfo) {
      if (!commentInfo) return false
      return commentInfo.avg_scores || commentInfo.total_scores || commentInfo.comment_record
    },

    safeGet(obj, path, defaultValue = '') {
      if (!obj) return defaultValue
      const keys = path.split('.')
      let result = obj
      for (const key of keys) {
        if (result && typeof result === 'object' && key in result) {
          result = result[key]
        } else {
          return defaultValue
        }
      }
      return result !== undefined && result !== null ? result : defaultValue
    },

    safeArray(arr) {
      if (!arr) return []
      return Array.isArray(arr) ? arr : []
    },

    safeString(str, defaultValue = '') {
      return str || defaultValue
    },

    // 处理图片加载错误
    handleImageError(event) {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY3Ii8+CjxwYXRoIGQ9Ik0xNTAgNzBDMTcwIDcwIDE4MCA4MCAxODAgMTAwQzE4MCA5NSAxNzUgOTUgMTcwIDEwMEMxNjUgMTE1IDE1NSAxMzUgMTUwIDEzNUMxNDUgMTM1IDEzNSAxMTUgMTMwIDEwMEMxMjUgOTUgMTMwIDk1IDE1MCA3MFoiIGZpbGw9IiNDQ0MiIvPgo8Y2lyY2xlIGN4PSIxNjAiIGN5PSIxNjAiIHI9IjQiIGZpbGw9IiNDQ0MiIvPgo8cGF0aCBkPSJNMTIwIDE2MEMxMjAgMTU1IDEyNSAxNTAgMTMwIDE1MEMxMzUgMTUwIDE0MCAxNTUgMTQwIDE2MEMxNDAgMTY1IDEzNSAxNzAgMTMwIDE3MEMxMjUgMTcwIDEyMCAxNjUgMTIwIDE2MFoiIGZpbGw9IiNDQ0MiIvPgo8dGV4dCB4PSIxNTAiIHk9IjE4NSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7llYblk4E8L3RleHQ+Cjwvc3ZnPgo='
    },

    // 处理排序值变更
    async handleSortChange(product, value) {
      if (!product || !product.prod_no) {
        this.$message.error('商品信息不完整')
        return
      }

      // 设置保存状态
      this.$set(product, 'sortSaving', true)
      this.$set(product, 'sortSaved', false)

      try {
        const res = await kkdayProductApi.setSort({
          prod_no: product.prod_no,
          sort: value
        })

        if (res && res.code === 200) {
          this.$message.success('排序值设置成功')
          this.$set(product, 'sortSaved', true)
          // 1.5秒后隐藏成功图标
          setTimeout(() => {
            this.$set(product, 'sortSaved', false)
          }, 1500)
        } else {
          this.$message.error(res.message || '设置排序值失败')
        }
      } catch (error) {
        console.error('设置排序值失败:', error)
        this.$message.error('设置排序值失败')
      } finally {
        this.$set(product, 'sortSaving', false)
      }
    },

    // 预览图片
    previewImage(imageUrl) {
      // 创建图片预览
      const image = new Image()
      image.src = imageUrl

      // 创建弹窗显示图片
      this.$alert('', '图片预览', {
        customClass: 'image-preview-dialog',
        showConfirmButton: true,
        confirmButtonText: '关闭',
        message: this.$createElement('div', {
          class: 'image-preview-container',
          domProps: {
            innerHTML: `<img src="${imageUrl}" style="max-width: 100%; max-height: 70vh; object-fit: contain;" />`
          }
        })
      })
    },

    // 从列表直接提交商品数据到后端
    async submitProductFromList(product) {
      if (!product || !product.prod_no) {
        this.$message.error('没有可提交的商品数据')
        return
      }

      // 保存待提交的商品
      this.pendingSubmitProduct = product
      
      // 先获取分类列表并显示选择对话框
      await this.showCategoryDialog()
    },

    // 提交商品数据到后端
    async submitProduct() {
      if (!this.productDetail || !this.productDetail.prod) {
        this.$message.error('没有可提交的商品数据')
        return
      }

      // 保存待提交的商品详情
      this.pendingSubmitProduct = this.productDetail.prod
      
      // 先获取分类列表并显示选择对话框
      await this.showCategoryDialog()
    },

    // 显示分类选择对话框
    async showCategoryDialog() {
      this.categoryLoading = true
      this.selectedCategoryId = null
      this.selectedCategoryPath = []
      this.selectedCityId = null

      try {
        // 获取分类列表
        const res = await vdmApi.getGoodsCategoryList()
        if (res && res.code === 1) {
          this.categoryList = res.data.list || []
          this.categoryDialogVisible = true
        } else {
          this.$message.error(res.message || '获取商品分类失败')
        }
      } catch (error) {
        console.error('获取商品分类失败:', error)
        this.$message.error('获取商品分类失败')
      } finally {
        this.categoryLoading = false
      }
    },

    // 处理分类选择
    handleCategoryCheck(data, checked) {
      const checkedNodes = this.$refs.categoryTree.getCheckedNodes()
      if (checkedNodes.length > 0) {
        this.selectedCategoryId = checkedNodes[0].id
        // 获取从根到当前节点的路径
        this.selectedCategoryPath = this.getCategoryPath(data)
      } else {
        this.selectedCategoryId = null
        this.selectedCategoryPath = []
      }
    },

    // 获取分类路径（从根到当前节点）
    getCategoryPath(nodeData) {
      const path = []
      // 使用 el-tree 的 getNode 方法获取完整节点信息
      const node = this.$refs.categoryTree.getNode(nodeData.id)
      if (node) {
        let currentNode = node
        // 从当前节点向上遍历，跳过没有 data.id 的根节点
        while (currentNode && currentNode.data && currentNode.data.id) {
          path.unshift(currentNode.data.id)
          currentNode = currentNode.parent
        }
      }

      // 如果通过 tree API 获取失败，使用 parent_id 回退方案
      if (path.length === 0 || (path.length === 1 && path[0] === null)) {
        return this.buildPathFromParentId(nodeData)
      }

      return path
    },

    // 基于 parent_id 构建路径的回退方法
    buildPathFromParentId(nodeData) {
      const path = []
      let currentId = nodeData.id

      // 首先添加当前节点
      path.push(currentId)

      // 向上查找父节点
      while (currentId !== null && currentId !== undefined) {
        const node = this.findNodeInTree(this.categoryList, currentId)
        if (node && node.parent_id) {
          path.unshift(node.parent_id)
          currentId = node.parent_id
        } else {
          break
        }
      }

      return path
    },

    // 在树中查找节点
    findNodeInTree(nodes, id) {
      for (const node of nodes) {
        if (node.id === id) {
          return node
        }
        if (node.children && node.children.length > 0) {
          const found = this.findNodeInTree(node.children, id)
          if (found) return found
        }
      }
      return null
    },

    // 确认提交（带分类）
    async confirmSubmitWithCategory() {
      if (!this.selectedCategoryId) {
        this.$message.warning('请选择商品分类')
        return
      }

      // 关闭对话框
      this.categoryDialogVisible = false

      // 判断是批量提交还是单个提交
      if (this.selectedProducts.length > 0) {
        await this.doBatchSubmit()
      } else {
        await this.doSubmitProduct()
      }
    },

    // 执行提交商品
    async doSubmitProduct() {
      if (!this.pendingSubmitProduct) {
        this.$message.error('没有可提交的商品数据')
        return
      }

      const product = this.pendingSubmitProduct
      const prod_no = product.prod_no

      if (!prod_no) {
        this.$message.error('商品编号不存在')
        return
      }

      // 设置提交状态
      if (product.submitting !== undefined) {
        this.$set(product, 'submitting', true)
      } else {
        this.submitLoading = true
      }

      try {
        // 构建提交数据，拆分分类为 cate_id 和 cate_sub_id
        const submitData = {
          prod_no: prod_no,
          cate_id: this.selectedCategoryPath[0] || null,
          cate_sub_id: this.selectedCategoryPath[1] || null,
          city_id: this.selectedCityId || null
        }

        console.log('提交数据:', submitData)

        // 调用后端API
        const res = await vdmApi.pushProduct(submitData)

        if (res && res.code === 200) {
          this.$message.success('商品数据提交成功！')
          console.log('提交成功:', res)
        } else {
          this.$message.error(res.message || '提交失败')
          console.error('提交失败:', res)
        }
      } catch (error) {
        console.error('提交商品数据失败:', error)
        this.$message.error('提交商品数据失败')
      } finally {
        // 清除提交状态
        if (product.submitting !== undefined) {
          this.$set(product, 'submitting', false)
        } else {
          this.submitLoading = false
        }
        // 清空待提交的商品
        this.pendingSubmitProduct = null
        this.selectedCategoryId = null
        this.selectedCategoryPath = []
      }
    },

    // 全选/取消全选
    handleSelectAll(checked) {
      this.filteredList.forEach(product => {
        this.$set(product, 'checked', checked)
      })
      this.updateSelectedProducts()
    },

    // 单个产品选择变化
    handleProductCheck(product) {
      this.updateSelectedProducts()
    },

    // 更新选中的产品列表
    updateSelectedProducts() {
      this.selectedProducts = this.filteredList.filter(p => p.checked)
      this.selectAll = this.selectedProducts.length === this.filteredList.length && this.filteredList.length > 0
      this.isIndeterminate = this.selectedProducts.length > 0 && this.selectedProducts.length < this.filteredList.length
    },

    // 判断产品是否被选中
    isProductSelected(product) {
      return product.checked || false
    },

    // 批量提交产品
    async batchSubmitProducts() {
      if (this.selectedProducts.length === 0) {
        this.$message.warning('请先选择要提交的商品')
        return
      }

      // 显示分类选择对话框
      await this.showCategoryDialog()
    },

    // 执行批量提交
    async doBatchSubmit() {
      if (this.selectedProducts.length === 0) {
        this.$message.error('没有可提交的商品数据')
        return
      }

      this.batchSubmitting = true
      const successList = []
      const failList = []

      try {
        // 逐个提交商品
        for (const product of this.selectedProducts) {
          try {
            const submitData = {
              prod_no: product.prod_no,
              cate_id: this.selectedCategoryPath[0] || null,
              cate_sub_id: this.selectedCategoryPath[1] || null,
              city_id: this.selectedCityId || null
            }

            const res = await vdmApi.pushProduct(submitData)

            if (res && res.code === 200) {
              successList.push(product.prod_no)
            } else {
              failList.push({ prod_no: product.prod_no, msg: res.message || '提交失败' })
            }
          } catch (error) {
            failList.push({ prod_no: product.prod_no, msg: error.message || '请求失败' })
          }
        }

        // 显示提交结果
        if (successList.length > 0) {
          this.$message.success(`成功提交 ${successList.length} 个商品`)
        }
        if (failList.length > 0) {
          console.error('提交失败的商品:', failList)
          this.$message.warning(`${failList.length} 个商品提交失败，请查看控制台详情`)
        }

        // 清空选择
        this.clearSelection()

      } catch (error) {
        console.error('批量提交失败:', error)
        this.$message.error('批量提交商品数据失败')
      } finally {
        this.batchSubmitting = false
      }
    },

    // 清空选择
    clearSelection() {
      this.filteredList.forEach(product => {
        this.$set(product, 'checked', false)
      })
      this.selectedProducts = []
      this.selectAll = false
      this.isIndeterminate = false
    },

    // 显示批量输入产品编号对话框
    showBatchInputDialog() {
      this.batchInputText = ''
      this.batchInputDialogVisible = true
    },

    // 解析产品编号列表
    parseProductNumbers() {
      if (!this.batchInputText || !this.batchInputText.trim()) {
        return []
      }

      // 按行分割，去除空行和空白字符
      const lines = this.batchInputText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)

      // 去重
      const uniqueLines = [...new Set(lines)]

      return uniqueLines
    },

    // 获取解析后的产品编号数量
    getParsedProductCount() {
      return this.parseProductNumbers().length
    },

    // 提交批量输入
    async submitBatchInput() {
      const productNumbers = this.parseProductNumbers()

      if (productNumbers.length === 0) {
        this.$message.warning('请输入产品编号')
        return
      }

      this.batchInputSubmitting = true

      try {
        // 构造临时的商品对象列表
        const tempProducts = productNumbers.map(prodNo => ({
          prod_no: prodNo,
          checked: true
        }))

        // 设置为选中的商品
        this.selectedProducts = tempProducts

        // 关闭输入对话框
        this.batchInputDialogVisible = false

        // 显示分类选择对话框
        await this.showCategoryDialog()

      } catch (error) {
        console.error('批量输入失败:', error)
        this.$message.error('批量输入失败')
      } finally {
        this.batchInputSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.vdm-list-page {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  .page-title {
    margin: 0;
    color: #2c3e50;
    font-size: 24px;
    font-weight: 600;

    i {
      margin-right: 10px;
      color: #409eff;
    }
  }
}

.filter-card {
  margin-bottom: 20px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-list-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-left {
      display: flex;
      align-items: center;
      gap: 15px;

      .selected-count {
        color: #409eff;
        font-size: 14px;
      }
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 10px;

      .el-tag {
        font-size: 12px;
      }
    }
  }
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
  min-height: 200px;
}

.product-item {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &.is-selected {
    border-color: #409eff;
    background: #f0f9ff;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .product-checkbox {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    padding: 4px;
  }

  .product-content {
    cursor: pointer;
  }
}

.product-image {
  position: relative;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  .product-type-badge {
    position: absolute;
    top: 10px;
    left: 10px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
  }
}

.product-info {
  padding: 15px;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;

  .product-name {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    line-height: 1.4;
    flex: 1;
    margin-right: 10px;
  }
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 5px;
}

.product-sort-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;

  .sort-label {
    font-size: 12px;
    color: #606266;
    font-weight: 500;
    min-width: 40px;
  }

  .sort-input {
    flex: 1;
    max-width: 140px;
  }

  .sort-loading {
    color: #409eff;
    font-size: 14px;
    animation: rotate 1s linear infinite;
  }

  .sort-success {
    color: #67c23a;
    font-size: 14px;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.destinations {
  margin-bottom: 10px;

  .destination-tag {
    margin-right: 5px;
    margin-bottom: 5px;

    i {
      margin-right: 2px;
    }
  }
}

.rating-info {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  .rating-count {
    margin-left: 10px;
    font-size: 12px;
    color: #909399;
  }
}

.introduction {
  margin-bottom: 15px;

  p {
    margin: 0;
    color: #606266;
    font-size: 13px;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.price-info {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 10px;

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .price-label {
    font-size: 12px;
    color: #909399;
  }

  .price-value {
    font-weight: 600;

    &.b2b-price {
      color: #409eff;
    }

    &.b2c-price {
      color: #67c23a;
    }

    &.profit {
      &.high-profit {
        color: #f56c6c;
      }

      &.medium-profit {
        color: #e6a23c;
      }

      &.low-profit {
        color: #909399;
      }
    }
  }

  .profit-percent {
    font-size: 12px;
    margin-left: 5px;
  }
}

.product-status {
  display: flex;
  gap: 5px;
}

.product-actions {
  margin-top: 10px;
  display: flex;
  justify-content: center;

  .el-button {
    width: 100%;
    font-size: 12px;

    &.is-loading {
      pointer-events: none;
    }
  }
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #909399;

  i {
    font-size: 64px;
    margin-bottom: 20px;
    display: block;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.pagination-container {
  margin-top: 20px;
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

// 产品详情弹窗样式
.product-detail-dialog {
  .product-detail {
    .detail-image {
      text-align: center;

      img {
        width: 100%;
        max-width: 300px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
    }

    .detail-info {
      h2 {
        margin-top: 0;
        color: #2c3e50;
        margin-bottom: 15px;
      }

      .detail-meta {
        margin-bottom: 20px;

        .el-tag {
          margin-right: 10px;
        }
      }
    }

    .basic-info {
      border-bottom: 1px solid #e4e7ed;
      padding-bottom: 20px;
      margin-bottom: 20px;
    }

    .detail-loading {
      padding: 20px 0;
    }

    .detail-content {
      h3 {
        color: #2c3e50;
        margin-top: 0;
        margin-bottom: 15px;
      }

      .raw-data-section {
        margin-top: 15px;

        .raw-data-container {
          margin-top: 15px;
          max-height: 400px;
          overflow-y: auto;
          border: 1px solid #e4e7ed;
          border-radius: 4px;
          padding: 15px;
          background: #f8f9fa;

          pre {
            margin: 0;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            white-space: pre-wrap;
            word-wrap: break-word;
            color: #2c3e50;
          }
        }

        // 营销设置网格样式
        .marketing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 10px;
          margin-top: 10px;

          .marketing-item {
            display: flex;
            align-items: center;
            padding: 10px;
            border: 1px solid #e4e7ed;
            border-radius: 6px;
            background: #f8f9fa;

            .country-flag {
              font-weight: 600;
              margin-right: 10px;
              min-width: 30px;
              font-size: 14px;
              color: #2c3e50;
            }

            .settings {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }
          }
        }

        .simple-image-list {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 10px;
          align-items: center;
        }

        // 信息网格样式
        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 12px;
          margin-top: 10px;

          .info-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            background: #f8f9fa;
            border-radius: 6px;
            border: 1px solid #e4e7ed;

            .label {
              font-weight: 500;
              color: #606266;
              font-size: 13px;
            }

            .value {
              font-weight: 600;
              color: #2c3e50;
              font-size: 13px;
            }
          }
        }

        // 价格信息样式
        .price-info {
          background: #f0f9ff;
          padding: 15px;
          border-radius: 8px;
          border: 1px solid #bfdbfe;

          .price-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;

            &:last-child {
              margin-bottom: 0;
            }

            .price {
              font-weight: 600;
              color: #e74c3c;
              font-size: 16px;
            }
          }
        }

        // 语言列表样式
        .lang-list {
          margin-top: 10px;
        }

        // 城市信息样式
        .cities-info {
          .city-item {
            display: inline-flex;
            align-items: center;
            margin-right: 10px;
            margin-bottom: 8px;

            .city-code {
              margin-left: 5px;
              font-size: 12px;
              color: #909399;
            }
          }
        }

        // 标签样式
        .tags {
          margin-top: 10px;
        }

        // 产品类别样式
        .category-info {
          .category-item {
            margin-bottom: 10px;

            .label {
              font-weight: 500;
              color: #606266;
              display: inline-block;
              min-width: 80px;
            }

            .value {
              font-weight: 600;
              color: #2c3e50;
            }

            .sub-categories {
              display: inline-block;
              margin-left: 10px;
            }
          }
        }

        // 描述模块样式
        .description-module {
          margin-top: 10px;

          ul {
            margin: 8px 0;
            padding-left: 20px;

            li {
              margin-bottom: 5px;
              line-height: 1.5;
              color: #606266;
            }
          }

          p {
            margin: 8px 0;
            line-height: 1.6;
            color: #606266;
          }
        }

        // 图文介绍样式
        .graphic-item {
          margin-bottom: 20px;
          padding: 15px;
          border: 1px solid #e4e7ed;
          border-radius: 8px;
          background: #fafafa;

          .graphic-media {
            margin-bottom: 10px;
          }

          .graphic-desc {
            margin: 0;
            padding: 10px;
            background: white;
            border-radius: 4px;
            border-left: 4px solid #409eff;
            color: #606266;
            line-height: 1.6;
          }
        }

        // 体验地点样式
        .location-item {
          margin-bottom: 25px;
          padding: 20px;
          border: 1px solid #e4e7ed;
          border-radius: 8px;
          background: #fafafa;

          .location-name h5 {
            margin: 0 0 15px 0;
            color: #2c3e50;
            font-size: 16px;
            font-weight: 600;
          }

          .location-address {
            margin-bottom: 15px;

            p {
              margin: 5px 0;
              color: #606266;
            }
          }

          .arrival-desc {
            margin-bottom: 15px;

            p {
              margin: 5px 0;
              color: #606266;
            }
          }

          .active-time {
            margin-bottom: 15px;

            p {
              margin: 5px 0;
              color: #606266;
              font-weight: 500;
            }

            .time-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              font-size: 13px;

              th, td {
                padding: 8px 12px;
                border: 1px solid #e4e7ed;
                text-align: left;
              }

              th {
                background: #f5f7fa;
                font-weight: 600;
                color: #2c3e50;
              }

              td {
                background: white;
                color: #606266;
              }

              tbody tr:hover {
                background: #f5f7fa;
              }
            }
          }

          .location-photo {
            margin-top: 15px;
          }
        }
      }
    }

    .no-detail-data {
      text-align: center;
      padding: 40px 0;
    }

    .detail-section {
      margin-bottom: 25px;

      h4 {
        margin-top: 0;
        margin-bottom: 15px;
        color: #2c3e50;
        font-size: 16px;
        font-weight: 600;
        border-bottom: 2px solid #409eff;
        padding-bottom: 5px;
      }

      .info-item {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;

        .label {
          font-weight: 600;
          color: #606266;
          min-width: 100px;
          margin-right: 10px;
        }

        .value {
          color: #2c3e50;
          flex: 1;

          &.price-highlight {
            font-size: 16px;
            font-weight: 600;
            color: #f56c6c;
          }
        }
      }

      .destinations {
        .el-tag {
          margin-right: 8px;
          margin-bottom: 5px;
        }
      }

      .price-highlight {
        font-size: 18px;
        font-weight: 600;
        color: #f56c6c;
      }

      .profit-highlight {
        font-size: 16px;
        font-weight: 600;
        color: #67c23a;
      }

      .introduction-content {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 6px;
        max-height: 200px;
        overflow-y: auto;

        pre {
          margin: 0;
          white-space: pre-wrap;
          word-wrap: break-word;
          font-family: inherit;
          font-size: 13px;
          line-height: 1.6;
          color: #606266;
        }
      }

      // 图片画廊
      .image-gallery {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 15px;
        margin-top: 10px;

        .image-item {
          cursor: pointer;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease;

          &:hover {
            transform: scale(1.05);
          }

          img {
            width: 100%;
            height: 150px;
            object-fit: cover;
          }
        }
      }

      // 视频列表
      .video-list {
        .video-item {
          margin-bottom: 15px;

          iframe {
            width: 100%;
            height: 315px;
            border-radius: 8px;
          }
        }
      }

      // 评分卡片
      .rating-card {
        text-align: center;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 8px;

        .avg-score {
          font-size: 24px;
          font-weight: 600;
          color: #ff9900;
          margin-bottom: 10px;
        }

        .total-reviews {
          margin-top: 10px;
          color: #606266;
          font-size: 14px;
        }
      }

      .stat-item {
        text-align: center;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;

        .label {
          display: block;
          margin-bottom: 5px;
          color: #606266;
        }

        .value {
          display: block;
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
        }
      }

      // 营销设置网格
      .marketing-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 10px;

        .marketing-item {
          display: flex;
          align-items: center;
          padding: 10px;
          border: 1px solid #e4e7ed;
          border-radius: 6px;
          background: #f8f9fa;

          .country-flag {
            font-weight: 600;
            margin-right: 10px;
            min-width: 30px;
          }

          .settings {
            display: flex;
            flex-direction: column;
            gap: 5px;
          }
        }
      }

      // 描述模块
      .description-modules {
        .module-item {
          margin-bottom: 20px;
          border: 1px solid #e4e7ed;
          border-radius: 8px;
          overflow: hidden;

          h5 {
            margin: 0;
            padding: 15px 20px;
            background: #409eff;
            color: white;
            font-weight: 600;
          }

          .module-content {
            padding: 20px;

            .property-item {
              margin-bottom: 15px;

              strong {
                color: #2c3e50;
                margin-bottom: 5px;
                display: block;
              }

              p {
                margin: 5px 0;
                color: #606266;
                line-height: 1.6;
              }
            }

            .include-section, .not-include-section {
              margin-bottom: 15px;

              h6 {
                margin: 0 0 10px 0;
                color: #2c3e50;
              }

              ul {
                margin: 0;
                padding-left: 20px;

                li {
                  margin-bottom: 5px;
                  color: #606266;
                  line-height: 1.6;
                }
              }
            }

            .reminders-list {
              ul {
                margin: 0;
                padding-left: 20px;

                li {
                  margin-bottom: 10px;
                  color: #606266;
                  line-height: 1.6;
                }
              }
            }

            .graphic-info {
              .graphic-item {
                margin-bottom: 20px;

                .graphic-media {
                  margin-bottom: 10px;

                  img {
                    max-width: 300px;
                    height: 200px;
                    object-fit: cover;
                    border-radius: 8px;
                    cursor: pointer;
                    margin-right: 10px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

                    &:hover {
                      transform: scale(1.02);
                    }
                  }
                }

                .graphic-desc {
                  color: #606266;
                  line-height: 1.6;
                  margin: 10px 0;
                }
              }
            }

            .location-info {
              .location-item {
                .location-header {
                  display: flex;
                  align-items: center;
                  margin-bottom: 15px;

                  h6 {
                    margin: 0;
                    color: #2c3e50;
                    font-size: 16px;
                  }

                  .location-photo {
                    margin-left: 15px;

                    img {
                      width: 100px;
                      height: 80px;
                      object-fit: cover;
                      border-radius: 6px;
                      cursor: pointer;
                      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

                      &:hover {
                        transform: scale(1.05);
                      }
                    }
                  }
                }

                .opening-hours, .arrival-info, .address-info {
                  margin-bottom: 15px;

                  h6 {
                    margin: 0 0 10px 0;
                    color: #2c3e50;
                  }

                  p {
                    margin: 5px 0;
                    color: #606266;
                    line-height: 1.6;
                  }
                }
              }
            }

            .html-content {
              line-height: 1.6;
              color: #606266;

              p {
                margin-bottom: 10px;
              }

              ul, li {
                margin-bottom: 5px;
              }
            }

            .default-module {
              background: #f8f9fa;
              padding: 15px;
              border-radius: 6px;
              font-size: 12px;
              color: #606266;
              overflow-x: auto;

              pre {
                margin: 0;
                white-space: pre-wrap;
                word-wrap: break-word;
              }
            }
          }
        }
      }

      // 套餐信息
      .package-item {
        .package-card {
          .package-header {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .package-price {
              display: flex;
              gap: 10px;

              .b2b-price {
                color: #409eff;
                font-weight: 600;
              }

              .b2c-price {
                color: #67c23a;
                font-weight: 600;
              }
            }
          }

          .package-details {
            .info-item {
              margin-bottom: 10px;
            }
          }
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .product-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .vdm-list-page {
    padding: 15px;
  }
}
</style>